<template>
  <div class="messaging-link-generator">
    <div class="info-banner">
      <i class="fa fa-info-circle"></i>
      Quickly generate deep links for WhatsApp, Messenger, or Line. Links are not saved — copy them to use in campaigns, nudges, or external channels.
    </div>

    <!-- Platform Selection -->
    <div class="section">
      <label class="section-label required">Platform</label>
      <div class="platform-cards">
        <div
          class="platform-card"
          :class="{ active: selectedPlatform === 'whatsapp' }"
          @click="selectPlatform('whatsapp')"
        >
          <div class="platform-check" v-if="selectedPlatform === 'whatsapp'"><i class="fa fa-check"></i></div>
          <div class="platform-icon whatsapp-icon">
            <i class="fa fa-whatsapp"></i>
          </div>
          <div class="platform-name">WhatsApp</div>
          <div class="platform-sub">wa.me link</div>
        </div>
        <div
          class="platform-card"
          :class="{ active: selectedPlatform === 'messenger' }"
          @click="selectPlatform('messenger')"
        >
          <div class="platform-check" v-if="selectedPlatform === 'messenger'"><i class="fa fa-check"></i></div>
          <div class="platform-icon messenger-icon">
            <i class="fa fa-facebook-official"></i>
          </div>
          <div class="platform-name">Messenger</div>
          <div class="platform-sub">m.me link</div>
        </div>
        <div
          class="platform-card"
          :class="{ active: selectedPlatform === 'line' }"
          @click="selectPlatform('line')"
        >
          <div class="platform-check" v-if="selectedPlatform === 'line'"><i class="fa fa-check"></i></div>
          <div class="platform-icon line-icon">
            <i class="fa fa-commenting"></i>
          </div>
          <div class="platform-name">Line</div>
          <div class="platform-sub">line.me link</div>
        </div>
      </div>
    </div>

    <!-- Country & Mapped Number -->
    <div class="section">
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label class="field-label required">Country</label>
            <nv-select class="form-control" v-model="selectedCountry" @change="onCountryChange">
              <option value="">-- Select Country --</option>
              <option v-for="country in availableCountries" :key="country" :value="country">
                {{ country }}
              </option>
            </nv-select>
            <small class="helper-text" v-if="selectedCountry && selectedPlatform === 'whatsapp'">
              WhatsApp Business number for {{ selectedCountry }}
            </small>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="field-label">Mapped Number / ID</label>
            <input
              type="text"
              class="form-control mapped-input"
              :value="mappedId"
              readonly
              placeholder="Select a country"
            />
            <small class="helper-text">Pulled from system configuration</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Pre-filled Message -->
    <div class="section">
      <label class="section-label">Pre-filled Message <span class="optional-tag">(optional)</span></label>
      <div class="message-tabs">
        <button
          type="button"
          class="msg-tab-btn"
          :class="{ active: messageType === 'keyword' }"
          @click="switchMessageType('keyword')"
        ><i class="fa fa-key"></i> Keyword</button>
        <button
          type="button"
          class="msg-tab-btn"
          :class="{ active: messageType === 'phrase' }"
          @click="switchMessageType('phrase')"
        ><i class="fa fa-comment-o"></i> Phrase</button>
        <button
          type="button"
          class="msg-tab-btn"
          :class="{ active: messageType === 'custom' }"
          @click="switchMessageType('custom')"
        ><i class="fa fa-pencil"></i> Custom Text</button>
      </div>

      <div class="message-input-area">
        <div v-if="messageType === 'keyword'">
          <treeselect
            v-model="selectedKeyword"
            :options="filteredKeywordOptions"
            :clearable="true"
            :searchable="true"
            :disabled="!selectedCountry"
            placeholder="-- Search & select a keyword --"
          />
          <small v-if="!selectedCountry" class="helper-text">Select a country first</small>
          <small v-else-if="filteredKeywordOptions.length === 0" class="helper-text helper-warning">No keywords found for {{ selectedCountry }}</small>
        </div>

        <div v-if="messageType === 'phrase'">
          <treeselect
            v-model="selectedPhrase"
            :options="phraseOptions"
            :clearable="true"
            :searchable="true"
            :disabled="!selectedCountry"
            placeholder="-- Search & select a phrase --"
          />
          <small v-if="!selectedCountry" class="helper-text">Select a country first</small>
          <small v-else-if="phraseOptions.length === 0 && !loadingPhrases" class="helper-text helper-warning">No phrases found for {{ selectedCountry }}</small>
          <small v-if="loadingPhrases" class="helper-text"><i class="fa fa-spinner fa-spin"></i> Loading phrases...</small>
        </div>

        <input
          v-if="messageType === 'custom'"
          type="text"
          class="form-control"
          v-model="customText"
          placeholder="Enter custom pre-filled text..."
        />
      </div>
    </div>

    <!-- Generated Link -->
    <div class="section generated-section" v-if="generatedLink">
      <label class="section-label generated-label">GENERATED LINK</label>
      <div class="generated-link-row">
        <div class="link-display">
          <i class="fa fa-link link-icon"></i>
          <span>{{ generatedLink }}</span>
        </div>
        <div class="link-actions">
          <button class="btn btn-action btn-copy" @click="copyLink" title="Copy to clipboard">
            <i class="fa fa-copy"></i> Copy
          </button>
          <button class="btn btn-action btn-qr" :class="{ 'btn-qr-active': showQr }" @click="toggleQr" title="Generate QR Code">
            <i class="fa fa-qrcode"></i> QR
          </button>
          <button class="btn btn-action btn-visit" @click="visitLink" title="Open link in new tab">
            <i class="fa fa-external-link"></i> Visit Link
          </button>
        </div>
      </div>

      <!-- QR Code Display -->
      <div v-if="showQr" class="qr-section">
        <div class="qr-container">
          <div class="qr-header">
            <span class="qr-title">QR Code Preview</span>
          </div>
          <img v-if="qrPreviewUrl" :src="qrPreviewUrl" alt="QR Code" class="qr-image" />
          <div class="qr-link-text">{{ generatedLink }}</div>
          <div class="qr-actions">
            <button class="btn btn-primary btn-sm" @click="downloadQr">
              <i class="fa fa-download"></i> Download QR (1024 x 1024)
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state when no link generated yet but country is selected -->
    <div class="section empty-state" v-if="selectedCountry && mappedId && !generatedLink">
      <div class="empty-state-content">
        <i class="fa fa-link"></i>
        <p>Select a keyword, phrase, or enter custom text to generate a link — or your link will appear here once a country is selected.</p>
      </div>
    </div>

    <BlockUI v-if="loading"><i class="fa fa-cog fa-spin fa-3x fa-fw"></i><br/>Please wait...</BlockUI>
  </div>
</template>

<script>
import api from '../../../utils/api'
import QRCode from 'qrcode'
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'

export default {
  name: 'MessagingLinkGenerator',
  components: {
    Treeselect
  },
  data () {
    return {
      loading: false,
      loadingPhrases: false,
      selectedPlatform: 'whatsapp',
      selectedCountry: '',
      messageType: 'keyword',
      selectedKeyword: null,
      selectedPhrase: null,
      customText: '',
      showQr: false,
      qrPreviewUrl: '',
      qrDownloadUrl: '',
      // Config from backend
      config: {
        whatsapp: {},
        messenger: {},
        line: {}
      },
      // Raw data from APIs
      allKeywords: [],
      phraseOptions: []
    }
  },
  computed: {
    availableCountries () {
      const platformConfig = this.config[this.selectedPlatform] || {}
      return Object.keys(platformConfig).sort()
    },
    mappedId () {
      if (!this.selectedCountry || !this.selectedPlatform) return ''
      const platformConfig = this.config[this.selectedPlatform] || {}
      const val = platformConfig[this.selectedCountry]
      if (!val) return ''
      if (typeof val === 'string') return val
      if (val.id) return val.id
      return ''
    },
    filteredKeywordOptions () {
      if (!this.selectedCountry) return []
      const country = this.selectedCountry.toLowerCase()
      return this.allKeywords
        .filter(kw => {
          const kwCountry = (kw.keyword_with_country || '').toLowerCase()
          return kwCountry.includes(country)
        })
        .map(kw => ({
          id: kw.keyword,
          label: kw.keyword
        }))
    },
    prefilledText () {
      if (this.messageType === 'keyword') return this.selectedKeyword || ''
      if (this.messageType === 'phrase') return this.selectedPhrase || ''
      if (this.messageType === 'custom') return this.customText
      return ''
    },
    generatedLink () {
      if (!this.selectedCountry || !this.mappedId) return ''

      const text = this.prefilledText
      const encodedText = text ? encodeURIComponent(text) : ''

      if (this.selectedPlatform === 'whatsapp') {
        const base = `https://wa.me/${this.mappedId}`
        return encodedText ? `${base}?text=${encodedText}` : base
      }

      if (this.selectedPlatform === 'messenger') {
        const base = `https://m.me/${this.mappedId}`
        return encodedText ? `${base}?text=${encodedText}` : base
      }

      if (this.selectedPlatform === 'line') {
        const base = `https://line.me/R/oaMessage/${this.mappedId}`
        return encodedText ? `${base}/?${encodedText}` : base
      }

      return ''
    }
  },
  watch: {
    async generatedLink (newLink) {
      if (!this.showQr) return
      if (!newLink) {
        this.showQr = false
        this.qrPreviewUrl = ''
        this.qrDownloadUrl = ''
        return
      }
      try {
        this.qrPreviewUrl = await QRCode.toDataURL(newLink, {
          errorCorrectionLevel: 'H',
          margin: 1,
          width: 256
        })
        this.qrDownloadUrl = await QRCode.toDataURL(newLink, {
          errorCorrectionLevel: 'H',
          margin: 1,
          width: 1024
        })
      } catch (err) {
        console.error('QR regeneration failed', err)
      }
    }
  },
  methods: {
    selectPlatform (platform) {
      this.selectedPlatform = platform
      this.selectedCountry = ''
      this.selectedKeyword = null
      this.selectedPhrase = null
      this.customText = ''
      this.phraseOptions = []
      this.showQr = false
      this.qrPreviewUrl = ''
      this.qrDownloadUrl = ''
    },
    switchMessageType (type) {
      this.messageType = type
      this.selectedKeyword = null
      this.selectedPhrase = null
      this.customText = ''
    },
    async onCountryChange () {
      this.selectedKeyword = null
      this.selectedPhrase = null
      this.customText = ''
      this.showQr = false
      this.qrPreviewUrl = ''
      this.qrDownloadUrl = ''
      if (this.selectedCountry) {
        await this.fetchPhrases(this.selectedCountry)
      } else {
        this.phraseOptions = []
      }
    },
    copyLink () {
      const node = document.createElement('textarea')
      node.value = this.generatedLink
      document.body.appendChild(node)
      node.select()
      try {
        const success = document.execCommand('copy')
        if (success) {
          this.$swal({
            type: 'success',
            title: 'Link copied to clipboard!',
            showConfirmButton: false,
            timer: 1000
          })
        }
      } catch (e) {
        console.log('Copy failed', e)
      }
      document.body.removeChild(node)
    },
    async toggleQr () {
      if (this.showQr) {
        this.showQr = false
        return
      }
      try {
        // Preview QR (smaller, for display)
        this.qrPreviewUrl = await QRCode.toDataURL(this.generatedLink, {
          errorCorrectionLevel: 'H',
          margin: 1,
          width: 256
        })
        // Download QR (1024x1024, high quality)
        this.qrDownloadUrl = await QRCode.toDataURL(this.generatedLink, {
          errorCorrectionLevel: 'H',
          margin: 1,
          width: 1024
        })
        this.showQr = true
      } catch (err) {
        console.error('QR generation failed', err)
      }
    },
    downloadQr () {
      if (!this.qrDownloadUrl) return
      const link = document.createElement('a')
      const text = this.prefilledText || 'link'
      link.download = `qr-${this.selectedPlatform}-${this.selectedCountry}-${text}.png`
      link.href = this.qrDownloadUrl
      link.click()
    },
    visitLink () {
      if (this.generatedLink) {
        window.open(this.generatedLink, '_blank')
      }
    },
    async fetchConfig () {
      try {
        const { data } = await api.buildRequest('getQuickLinkConfig', 'POST')
        if (data && data.payload) {
          this.config = data.payload
        }
      } catch (err) {
        console.error('Failed to fetch quick link config', err)
      }
    },
    async fetchKeywords () {
      try {
        const { data } = await api.buildRequest('getKeywordList', 'GET')
        if (data && data.payload) {
          this.allKeywords = data.payload
        }
      } catch (err) {
        console.error('Failed to fetch keywords', err)
      }
    },
    async fetchPhrases (country) {
      this.loadingPhrases = true
      try {
        const { data } = await api.buildRequest('getPhraseList', 'POST', {
          selector: { country: country },
          sort: { createdAt: -1 }
        })
        if (data && data.payload && data.payload.phrases) {
          this.phraseOptions = data.payload.phrases.map(ph => ({
            id: ph.phrase,
            label: `${ph.phrase} (${ph.keyword})`
          }))
        } else {
          this.phraseOptions = []
        }
      } catch (err) {
        console.error('Failed to fetch phrases', err)
        this.phraseOptions = []
      }
      this.loadingPhrases = false
    }
  },
  async mounted () {
    this.loading = true
    await Promise.all([
      this.fetchConfig(),
      this.fetchKeywords()
    ])
    this.loading = false
  }
}
</script>

<style scoped>
.messaging-link-generator {
  position: relative;
  max-width: 100%;
}

/* Info Banner */
.info-banner {
  background: linear-gradient(135deg, #e8f4fd 0%, #d6eef8 100%);
  border: 1px solid #b8daec;
  border-left: 4px solid #0D9488;
  border-radius: 4px;
  padding: 14px 18px;
  margin-bottom: 28px;
  color: #1a6c8a;
  font-size: 13px;
  line-height: 1.5;
}

.info-banner i {
  margin-right: 8px;
  font-size: 15px;
}

/* Sections */
.section {
  margin-bottom: 28px;
}

.section-label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 12px;
  color: #2c3e50;
}

.section-label.required::after {
  content: ' *';
  color: #e74c3c;
}

.field-label {
  display: block;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 6px;
  color: #2c3e50;
}

.field-label.required::after {
  content: ' *';
  color: #e74c3c;
}

.optional-tag {
  font-weight: 400;
  color: #95a5a6;
  font-size: 12px;
}

.helper-text {
  display: block;
  margin-top: 4px;
  color: #95a5a6;
  font-size: 12px;
}

.helper-warning {
  color: #e67e22;
}

/* Platform Cards */
.platform-cards {
  display: flex;
  gap: 16px;
}

.platform-card {
  flex: 1;
  position: relative;
  border: 2px solid #e8ecef;
  border-radius: 10px;
  padding: 24px 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.25s ease;
  background: #fff;
}

.platform-card:hover {
  border-color: #a0cfeb;
  background-color: #fafcfe;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(13, 148, 136, 0.1);
}

.platform-card.active {
  border-color: #0D9488;
  background: linear-gradient(180deg, #f0f8ff 0%, #e8f2fa 100%);
  box-shadow: 0 4px 14px rgba(13, 148, 136, 0.18);
}

.platform-check {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #0D9488;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
}

.platform-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
  font-size: 26px;
  color: #fff;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
}

.whatsapp-icon {
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
}

.messenger-icon {
  background: linear-gradient(135deg, #00B2FF 0%, #006AFF 100%);
}

.line-icon {
  background: linear-gradient(135deg, #00C300 0%, #00B900 100%);
}

.platform-name {
  font-weight: 600;
  font-size: 14px;
  color: #2c3e50;
  margin-bottom: 2px;
}

.platform-sub {
  font-size: 11px;
  color: #95a5a6;
}

/* Mapped input */
.mapped-input {
  background-color: #f8f9fa !important;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  color: #555 !important;
}

/* Message Tabs */
.message-tabs {
  display: flex;
  margin-bottom: 14px;
  border-bottom: 2px solid #e8ecef;
}

.msg-tab-btn {
  padding: 10px 22px;
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #7f8c8d;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: -2px;
  transition: all 0.2s ease;
}

.msg-tab-btn i {
  margin-right: 5px;
  font-size: 12px;
}

.msg-tab-btn:hover {
  color: #0D9488;
  background-color: #f8fbfd;
}

.msg-tab-btn.active {
  color: #0D9488;
  font-weight: 600;
  border-bottom-color: #0D9488;
}

.message-input-area {
  margin-top: 2px;
}

/* Generated Link Section */
.generated-section {
  background: #f8fbfd;
  border: 1px solid #d6eef8;
  border-radius: 8px;
  padding: 20px;
  margin-top: 8px;
}

.generated-label {
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 1.5px;
  color: #7f8c8d;
  font-weight: 700;
  margin-bottom: 10px;
}

.generated-link-row {
  display: flex;
  align-items: center;
  border: 1px solid #d6eef8;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.link-display {
  flex: 1;
  padding: 12px 16px;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  color: #2c3e50;
  overflow-x: auto;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
}

.link-icon {
  color: #0D9488;
  font-size: 14px;
  flex-shrink: 0;
}

.link-actions {
  display: flex;
  flex-shrink: 0;
}

.btn-action {
  border: none;
  border-left: 1px solid #e8ecef;
  border-radius: 0;
  padding: 12px 18px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-copy {
  background: #0D9488;
  color: #fff;
}

.btn-copy:hover {
  background: #0D9488;
  color: #fff;
}

.btn-qr {
  background: #f5f7f9;
  color: #555;
}

.btn-qr:hover,
.btn-qr-active {
  background: #e8f0f5;
  color: #0D9488;
}

.btn-visit {
  background: #f5f7f9;
  color: #555;
}

.btn-visit:hover {
  background: #e8f0f5;
  color: #0D9488;
}

/* QR Section */
.qr-section {
  margin-top: 20px;
}

.qr-container {
  background: #fff;
  border: 1px solid #e0e6ea;
  border-radius: 10px;
  padding: 24px;
  text-align: center;
  max-width: 360px;
  margin: 0 auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.qr-header {
  margin-bottom: 16px;
}

.qr-title {
  font-size: 13px;
  font-weight: 600;
  color: #7f8c8d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.qr-image {
  display: block;
  margin: 0 auto 12px;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
}

.qr-link-text {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 11px;
  color: #95a5a6;
  margin-bottom: 16px;
  word-break: break-all;
  padding: 0 8px;
}

.qr-actions {
  text-align: center;
}

.qr-actions .btn {
  padding: 8px 20px;
  font-size: 13px;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 32px 20px;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  background: #fdfdfe;
}

.empty-state-content i {
  font-size: 28px;
  color: #bdc3c7;
  margin-bottom: 10px;
  display: block;
}

.empty-state-content p {
  color: #95a5a6;
  font-size: 13px;
  margin: 0;
  line-height: 1.5;
}
</style>



// WEBPACK FOOTER //
// MessagingLinkGenerator.vue?62fbe743